// IMPORT REACT
import React, {
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'

// IMPORT UI COMPONENTS
import {
  Box,
  Divider,
  Grid,
  Typography
} from '@mui/material'

import CustomBreadcrumbs from '../../components/BreadcrumbsComponent/CustomBreadcrumbs'
import {
  useLocation,
  useNavigate
} from 'react-router-dom';
import { EncryptDecryptService } from '../../services/common/EncryptionService';
import { GetReferBookEntryDetail } from '../../services/ReferBookEntryListService';
import { PreferencesContext } from '../../PreferenceContext';
import { AlertType } from '../../services/models/AlertType';
import parse from "html-react-parser";
import dayjs from 'dayjs'
import SkeletonWrapper from '../../components/Skeleton/SkeletonWrapper';

// COMMON STYLES
const value = {
  color: "#000",
  marginLeft: "4px",
};

const filedsLabelText = {
  color: "#00000099",
  fontSize: "14px",
  padding: "0px 0px 0px 5px !important"
};

const ReferBookEntryDetails = () => {
  const edService = new EncryptDecryptService();
  const navigate = useNavigate();

  // STATES -- FOR ERROR HANDLING AND LOADING
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showSkeleton, setShowSkeleton] = useState<boolean>(true);

  const { userContext, updateUserContext } = useContext(PreferencesContext);
  const [referBookData, setReferBookData] = useState<any>();

  // Getting Seach Paramms for Pagination
  function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  }

  // GETTTING PARAMS
  let urlParams = useQuery();

  let referBookEntryId = urlParams.get("q");
  let curReferBookEntryId = 0;
  if (referBookEntryId !== null && referBookEntryId !== undefined) {
    let decId = edService.decrypt(referBookEntryId);
    if (
      decId !== "" &&
      decId !== null &&
      decId !== undefined
    ) {
      curReferBookEntryId = +decId;
    }
  } else {
    navigate('/not-found')
  }

  const getReferBookEntryDetailById = async () => {
    let ReferBookEntry: any;
    let params = new URLSearchParams();
    params.append("referBookEntryId", curReferBookEntryId?.toString());
    params.append("consumerId", userContext?.consumerId?.toString());
    params.append("organizationId", userContext?.organizationId?.toString());
    params.append("branchId", userContext?.branchId?.toString());

    try {
      const res: any = await GetReferBookEntryDetail(params);
      if (res) {
        let result = res?.data?.data;
        setReferBookData(result);
      }
      return ReferBookEntry;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // FETCH DATA
  const fetchData = async () => {
    try {
      if (curReferBookEntryId > 0) {
        await getReferBookEntryDetailById();
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
    }
  };

  // USE EFFECTS
  useEffect(() => {
    setIsLoading(true);
    fetchData();
  }, [referBookEntryId]);

  // HANDLE DELAY FOR SKELETON
  useEffect(() => {
    if (!isLoading) {
      const timeout = setTimeout(() => setShowSkeleton(false), 100); // Delay unmounting the Skeleton
      return () => clearTimeout(timeout);
    } else {
      setShowSkeleton(true);
    }
  }, [isLoading]);

  return (
    <Box>
      <Grid container className="page-header">
        <Grid item xs={6}>
          <Box sx={{ display: "flex", height: "100%", alignItems: "end" }}>
            <CustomBreadcrumbs />
          </Box>
        </Grid>
      </Grid>

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          paddingY: 3,
          flexDirection: "column",
          gap: 2,
        }}
      >
        <SkeletonWrapper
          isLoading={showSkeleton}
          className="skeleton-input"
        >
          {referBookData && Object.keys(referBookData).length > 0 ? (
            <Box
              className={"main-container"}
              sx={{
                width: "100%",
                maxWidth: 900,
                backgroundColor: "#fff",
                borderRadius: "10px",
                boxShadow: "0 4px 15px rgba(0,0,0,0.08)",
                p: 3,
                marginX: "auto",
              }}
            >
              <Grid item xs={12} mb={1.5}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: "18px !important",
                    marginLeft: "4px",
                    textAlign: "center"
                  }}
                >
                  {referBookData?.organizationName}
                </Typography>
              </Grid>

              <Divider sx={{ mb: 1 }} />

              <Grid item xs={12} mb={1.5}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: "18px !important",
                    marginLeft: "4px"
                  }}
                >
                  {referBookData?.toDoctorName}{" "}
                  {referBookData?.toDoctorQualification && (
                    <Typography
                      component="span"
                      sx={{
                        fontWeight: 500,
                        fontSize: "14px !important",
                      }}
                    >
                      ({referBookData.toDoctorQualification})
                    </Typography>
                  )}
                </Typography>
              </Grid>

              <Divider sx={{ mb: 1 }} />

              <Grid item xs={12} mb={1}>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 700,
                    fontSize: "16px !important",
                    marginLeft: "4px"
                  }}
                >
                  {referBookData?.toLocationName}
                </Typography>

                {/* Address Line */}
                <Typography variant="body2" sx={{ ...value, mt: 0.5 }}>
                  {[
                    referBookData?.toLocationAddress1,
                    referBookData?.toLocationAddress2,
                    referBookData?.toLocationCity,
                    referBookData?.toLocationCountry
                      ? `${referBookData?.toLocationCountry} - ${referBookData?.toLocationPostalCode}`
                      : referBookData?.toLocationPostalCode,
                  ]
                    .filter(Boolean)
                    .join(", ")}
                </Typography>

                {/* Mobile Numbers */}
                {(referBookData?.toLocationContactNumber ||
                  referBookData?.fromLocationAlternateContactNumber ||
                  referBookData?.toLocationEmail) && (
                    <Typography variant="body2" sx={{ ...value, mt: 0.5 }}>
                      {[
                        referBookData?.toLocationContactNumber
                          ? `Contact Number: ${referBookData?.toLocationContactNumber}`
                          : "",
                        referBookData?.fromLocationAlternateContactNumber
                          ? `${referBookData?.fromLocationAlternateContactNumber}`
                          : "",
                        referBookData?.toLocationEmail
                          ? `Email: ${referBookData?.toLocationEmail}`
                          : "",
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </Typography>
                  )}
              </Grid>

              <Divider sx={{ mb: 1 }} />

              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography sx={filedsLabelText}>
                    Ref Date:
                  </Typography>
                  <Typography variant="body1" sx={value}>
                    {referBookData?.referDate
                      ? dayjs(referBookData.referDate).format("DD-MM-YYYY")
                      : ""}
                  </Typography>
                </Grid>
              </Grid>

              <Grid container spacing={1} mt={0.2}>
                <Grid item xs={6}>
                  <Typography sx={filedsLabelText}>
                    Patient First Name:
                  </Typography>
                  <Typography variant="body1" sx={value}>
                    {referBookData?.forPatientFirstName}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography sx={filedsLabelText}>
                    Patient Last Name:
                  </Typography>
                  <Typography variant="body1" sx={value}>
                    {referBookData?.forPatientLastName}
                  </Typography>
                </Grid>

                {(referBookData?.forPatientGender > 0 ||
                  referBookData?.forPatientAgeYear > 0 ||
                  referBookData?.forPatientAgeMonth > 0 ||
                  referBookData?.forPatientAgeDay > 0) && (

                    <Grid item xs={6}>
                      <Typography sx={filedsLabelText}>
                        GENDER / AGE:
                      </Typography>

                      <Typography variant="body1" sx={value}>
                        {referBookData?.forPatientGender || null}
                        {(referBookData?.forPatientAgeYear > 0 ||
                          referBookData?.forPatientAgeMonth > 0 ||
                          referBookData?.forPatientAgeDay > 0) ? ' / ' : ''}

                        {referBookData?.forPatientAgeYear > 0
                          ? `${referBookData.forPatientAgeYear}y `
                          : ''}
                        {referBookData?.forPatientAgeMonth > 0
                          ? `${referBookData.forPatientAgeMonth}m `
                          : ''}
                        {referBookData?.forPatientAgeDay > 0
                          ? `${referBookData.forPatientAgeDay}d`
                          : ''}
                      </Typography>
                    </Grid>
                  )}

                {referBookData?.forPatientMobileNumber && (
                  <Grid item xs={6}>
                    <Typography sx={filedsLabelText}>
                      Patient Mobile Number:
                    </Typography>
                    <Typography variant="body1" sx={value}>
                      {referBookData?.forPatientMobileNumber}
                    </Typography>
                  </Grid>
                )}
              </Grid>


              {referBookData?.reasonForReferenceColl?.length > 0 && (
                <Grid item xs={12}>
                  <Divider sx={{ mb: 1, mt: 1 }} />
                  <Typography sx={filedsLabelText}>
                    Reason for Reference:
                  </Typography>
                  <Typography variant="body1" sx={value}>
                    {referBookData?.reasonForReferenceColl
                      .map((item: any) => item?.reasonForReferenceLabel)
                      .join(", ")}
                  </Typography>
                </Grid>
              )}

              {referBookData?.clinicalNotes && (
                <Grid item xs={12}>
                  <Divider sx={{ mb: 1, mt: 1 }} />
                  <Typography sx={filedsLabelText}>
                    Clinical Notes:
                  </Typography>
                  <Typography variant="body1" sx={value}>
                    {parse(
                      (referBookData?.clinicalNotes || "")
                        .replace(/^<p>/, "")
                        .replace(/<\/p>$/, "")
                    )}
                  </Typography>
                </Grid>
              )}

              <Divider sx={{ mb: 1, mt: 1 }} />

              {(referBookData?.fromDoctorName ||
                referBookData?.fromDoctorQualification ||
                referBookData?.fromDoctorContactNumber) && (
                  <Grid container spacing={1} mb={2}>
                    {(referBookData?.fromDoctorName &&
                      <Grid item xs={6}>
                        <Typography sx={filedsLabelText}>
                          Ref By Doctor (Your Name):
                        </Typography>
                        <Typography variant="body1" sx={value}>
                          {referBookData?.fromDoctorName}
                        </Typography>
                      </Grid>
                    )}

                    {(referBookData?.fromDoctorQualification &&
                      <Grid item xs={6}>
                        <Typography sx={filedsLabelText}>
                          Qualification:
                        </Typography>
                        <Typography variant="body1" sx={value}>
                          {referBookData?.fromDoctorQualification}
                        </Typography>
                      </Grid>
                    )}

                    {(referBookData?.fromDoctorContactNumber &&
                      <Grid item xs={6}>
                        <Typography sx={filedsLabelText}>
                          Contact Number:
                        </Typography>
                        <Typography variant="body1" sx={value}>
                          {referBookData?.fromDoctorContactNumber}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                )}
            </Box>
          ) : (
            <Box
              sx={{
                width: "100%",
                maxWidth: 900,
                marginX: "auto",
                backgroundColor: "#fff",
                borderRadius: "10px",
                boxShadow: "0 4px 15px rgba(0,0,0,0.08)",
                p: 4,
                textAlign: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: 600,
                  color: "text.secondary",
                }}
              >
                No summary found
              </Typography>
            </Box>
          )}
        </SkeletonWrapper>
      </Box>
    </Box>
  )
}

export default ReferBookEntryDetails
